import { useCallback, useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import {
  getAwDil, nastavCenuAwDil, schvalitAwDil, skrytAwDil, type AwDilDetail,
} from "../lib/api.js";
import { LoadingBlock } from "../ui/Spinner.js";
import { EmptyState } from "../ui/EmptyState.js";
import { Button } from "../ui/Button.js";
import { Badge } from "../ui/Badge.js";
import { PackageIcon } from "../lib/icons.js";

/**
 * Detail dílu Angel's Wheels — jedna karta na eshopu, varianty = barva
 * (nebo velikost kola u paprsků). Model × Rok z jejich webu je FITMENT
 * (píše se do `custom.kompatibilita_tab` jako KTM od 2017), ne varianta.
 */

const kc = (v: string | number | null): string =>
  v === null ? "—" : `${Math.round(Number(v)).toLocaleString("cs-CZ")} Kč`;

const SKLAD: Record<string, string> = { skladem: "skladem", malo: "méně než 5 ks", neni: "není skladem" };

export function AngelswheelsDilPage(): JSX.Element {
  const { ref = "" } = useParams();
  const [d, setD] = useState<AwDilDetail | null>(null);
  const [chyba, setChyba] = useState<string | null>(null);
  const [fotka, setFotka] = useState(0);
  const [edituji, setEdituji] = useState(false);
  const [hodnota, setHodnota] = useState("");
  const [pracuje, setPracuje] = useState(false);
  const [akceChyba, setAkceChyba] = useState<string | null>(null);

  const nacti = useCallback(async () => {
    try {
      const x = await getAwDil(ref);
      setD(x);
      setHodnota(x.naseCena ? String(Math.round(Number(x.naseCena))) : "");
    } catch (e) {
      setChyba(e instanceof Error ? e.message : "nepovedlo se");
    }
  }, [ref]);
  useEffect(() => { void nacti(); }, [nacti]);

  if (chyba) return <EmptyState icon={<PackageIcon size={28} />} title="Nenalezeno" description={chyba} />;
  if (!d) return <LoadingBlock label="Načítám…" />;

  const akce = async (fn: () => Promise<void>): Promise<void> => {
    setPracuje(true); setAkceChyba(null);
    try { await fn(); await nacti(); } catch (e) { setAkceChyba(e instanceof Error ? e.message : "nepovedlo se"); } finally { setPracuje(false); }
  };
  const ulozCenu = async (): Promise<void> => {
    await akce(() => nastavCenuAwDil(d.ref, hodnota.trim() === "" ? null : Number(hodnota)));
    setEdituji(false);
  };

  return (
    <div className="p-6 max-w-6xl">
      <nav className="mb-4 text-sm text-zinc-500">
        <Link to="/angelswheels" className="hover:text-ktm-orange">Angel&apos;s Wheels</Link>
        <span className="mx-2 text-zinc-300">›</span><span>{d.policeNazev}</span>
        <span className="mx-2 text-zinc-300">›</span><span className="font-mono">{d.ref}</span>
      </nav>

      <div className="grid gap-8 lg:grid-cols-2">
        <div>
          <div className="rounded-2xl border border-zinc-200 bg-zinc-50 aspect-square grid place-items-center overflow-hidden">
            {d.fotky[fotka] ? <img src={d.fotky[fotka]} alt={d.nazev} className="w-full h-full object-contain p-6" /> : <span className="text-zinc-300">bez fotky</span>}
          </div>
          {d.fotky.length > 1 && (
            <div className="mt-2 flex gap-2 flex-wrap">
              {d.fotky.map((f, i) => (
                <button key={f} type="button" onClick={() => setFotka(i)} className={`h-14 w-14 rounded border overflow-hidden ${i === fotka ? "border-ktm-orange" : "border-zinc-200"}`}>
                  <img src={f} alt="" className="h-full w-full object-contain" />
                </button>
              ))}
            </div>
          )}
        </div>

        <div>
          <div className="flex items-start gap-2">
            <h1 className="text-xl font-semibold flex-1">{d.nazev}</h1>
            {d.ktmFit && <Badge tone="amber">KTM</Badge>}
            {d.univerzal && <Badge tone="neutral">univerzál</Badge>}
          </div>
          <div className="mt-1 text-sm text-zinc-500">
            {d.policeNazev} · kód <span className="font-mono">{d.ref}</span> · u dodavatele: {d.kategorieDodavatele}
          </div>
          <a href={d.odkazDodavatele} target="_blank" rel="noreferrer" className="mt-1 inline-block text-sm text-ktm-orange hover:underline">otevřít u dodavatele ↗</a>
          {d.fitBrands.length > 0 && (
            <div className="mt-1 text-xs text-zinc-500">Tabulka „Bike models" u dodavatele: {d.fitBrands.join(", ")}</div>
          )}

          <div className="mt-4 rounded-xl border border-zinc-200 divide-y divide-zinc-100">
            <div className="flex justify-between px-4 py-2 text-sm">
              <span className="text-zinc-500">Cena dodavatele (s DPH)</span><span className="tabular-nums font-medium">{kc(d.cenaDodavatele)}</span>
            </div>
            <div className="flex justify-between px-4 py-2 text-sm">
              <span className="text-zinc-500">Dostupnost u dodavatele</span><span>{SKLAD[d.skladDodavatele] ?? d.skladDodavatele}</span>
            </div>
            <div className="flex justify-between px-4 py-2 text-sm">
              <span className="text-zinc-500">Karta v ABŘE / sklad Vlašim</span>
              <span>{d.abraKod ? <><code>{d.abraKod}</code> · <strong>{d.skladVlasim} ks</strong></> : <span className="text-zinc-400">nevedeme</span>}</span>
            </div>
            <div className="flex items-center justify-between px-4 py-2.5">
              <span className="text-sm font-medium">Naše cena na eshop</span>
              {edituji ? (
                <span className="inline-flex items-center gap-1">
                  <input type="number" value={hodnota} onChange={(e) => setHodnota(e.target.value)} className="w-28 rounded border border-slate-300 px-2 py-1 text-right" placeholder="prázdné = dodavatel" />
                  <Button size="sm" onClick={() => void ulozCenu()} disabled={pracuje}>Ulož</Button>
                  <Button size="sm" variant="ghost" onClick={() => setEdituji(false)}>×</Button>
                </span>
              ) : (
                <button type="button" onClick={() => setEdituji(true)} className="text-lg font-semibold tabular-nums hover:underline decoration-dotted" title={d.cenaRucne ? `Ručně přepsáno (dodavatel ${kc(d.naseCenaNavrzena)})` : "Klikni pro ruční cenu"}>
                  {kc(d.naseCena)}{d.cenaRucne && <span className="ml-1 text-amber-600 text-sm">✎</span>}
                </button>
              )}
            </div>
          </div>
          <p className="mt-2 text-[11px] text-zinc-400">
            💰 Cena = cena dodavatele 1 : 1 (Vít 8. 9. 2026). Dealerská sleva 10 % ⇒ marže ~10 %, proto je Angel&apos;s mimo zákaznické slevy.
          </p>

          <div className="mt-4 flex items-center gap-2">
            {d.stav === "na-eshopu" || d.stav === "ceka" ? (
              <Button variant="ghost" disabled={pracuje} onClick={() => void akce(() => skrytAwDil(d.ref))}>Skrýt z eshopu</Button>
            ) : (
              <Button disabled={pracuje} onClick={() => void akce(() => schvalitAwDil(d.ref))}>Pustit na eshop</Button>
            )}
            {d.stav === "na-eshopu" && <Badge tone="green">Na eshopu</Badge>}
            {d.stav === "ceka" && <Badge tone="blue">Čeká na robota</Badge>}
            {d.stav === "skryto" && <Badge tone="neutral">Skryto</Badge>}
            {d.shopifyHandle && (
              <a href={`https://ktm-vlasim.myshopify.com/products/${d.shopifyHandle}`} target="_blank" rel="noreferrer" className="text-sm text-zinc-500 hover:text-ktm-orange">karta na eshopu ↗</a>
            )}
          </div>
          {d.foundedError && <div className="mt-2 text-sm text-red-600">Robot hlásí: {d.foundedError}</div>}
          {akceChyba && <div className="mt-2 text-sm text-red-600">{akceChyba}</div>}
        </div>
      </div>

      <section className="mt-8">
        <h2 className="font-medium">Varianty — budoucí varianty na eshopu</h2>
        <p className="mt-1 text-sm text-zinc-500">
          Z jejich „variant" (Model × Rok × Barva) zůstává jen barva — Model a Rok je fitment (KTM od 2017).
        </p>
        <div className="mt-2 overflow-x-auto">
          <table className="w-full max-w-3xl text-sm">
            <thead>
              <tr className="text-xs uppercase tracking-wide text-zinc-400">
                <th className="py-2 pr-3 text-left">{d.varianty[0]?.paramName ?? "Volba"}</th>
                <th className="py-2 pr-3 text-left">SKU</th><th className="py-2 pr-3 text-right">Cena dodavatele</th>
                <th className="py-2 text-right">Sklad komponenty</th>
              </tr>
            </thead>
            <tbody>
              {d.varianty.map((v) => (
                <tr key={v.sku} className="border-t border-zinc-100">
                  <td className="py-1.5 pr-3 font-medium">{v.label}</td>
                  <td className="py-1.5 pr-3 font-mono text-xs">{v.sku}</td>
                  <td className="py-1.5 pr-3 text-right tabular-nums">{kc(v.cena)}</td>
                  <td className="py-1.5 text-right tabular-nums">{v.sklad > 4 ? "skladem" : v.sklad > 0 ? `${v.sklad} ks` : <span className="text-red-700">není</span>}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <p className="mt-2 text-[11px] text-zinc-400">
          🧨 „Sklad" v jejich HTML je sklad KOMPONENTY sdílený všemi kombinacemi — na eshop jde jen jako slovo.
        </p>
      </section>

      {d.popisHtml && (
        <section className="mt-8">
          <h2 className="font-medium">Popis od dodavatele</h2>
          <div className="mt-2 text-sm text-zinc-700 leading-relaxed max-w-3xl" dangerouslySetInnerHTML={{ __html: d.popisHtml }} />
        </section>
      )}

      {d.historieCen.length > 0 && (
        <section className="mt-8">
          <h2 className="font-medium">Jak se u dodavatele hýbala cena</h2>
          <table className="mt-2 w-full max-w-xl text-sm">
            <thead>
              <tr className="text-xs uppercase tracking-wide text-zinc-400">
                <th className="py-2 pr-3 text-left">Kdy</th><th className="py-2 pr-3 text-right">Cena</th><th className="py-2 text-right">Rozdíl</th>
              </tr>
            </thead>
            <tbody>
              {d.historieCen.map((h, i) => {
                const r = h.rozdil === null ? null : Number(h.rozdil);
                return (
                  <tr key={`${h.kdy}-${i}`} className="border-t border-zinc-100">
                    <td className="py-1.5 pr-3 text-zinc-500">{new Date(h.kdy).toLocaleString("cs-CZ")}</td>
                    <td className="py-1.5 pr-3 text-right tabular-nums">{kc(h.cena)}</td>
                    <td className={`py-1.5 text-right tabular-nums ${r === null ? "text-zinc-300" : r < 0 ? "text-green-700" : r > 0 ? "text-red-700" : "text-zinc-400"}`}>
                      {r === null ? "první záznam" : `${r > 0 ? "+" : ""}${Math.round(r).toLocaleString("cs-CZ")} Kč`}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </section>
      )}
    </div>
  );
}
